import {
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  Row,
  Form,
  Button,
  ButtonGroup,
} from "reactstrap";
import React, { useEffect, useState } from "react";
import {
  addVariable,
  updateVariable,
  restoreVariable,
  releaseVariable,
} from "@/services/variableServices/variableActions";
// nodejs library that concatenates classes
import classnames from "classnames";
// select Input
import Select from "react-select";
// react plugin for creating notifications over the dashboard
import { ToastContainer } from "react-toastify";
import ImageUploadContainer from "../../User/adding-user/ImageUploadContainer";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import NotificationAlertOptions from "@/layouts/Alerts";
import getErrorMessage, { hasDisplayableError } from "@/utils/getErrorMessage";
import { asCaught } from "@/services/caughtError";

const variableTypes = [
  { value: "content", label: "Content variable" },
  { value: "setting", label: "Settings variable" },
];
// Only the types a live template, site or page uses. No scalar Product or Post
// (SCRUM-2205): the site reads a page's product/post from
// page_configuration.object_target. No Ad, Category, Subcategory, Blog, Json,
// tips or pros/cons (SCRUM-2207): none is declared in production (measured
// 2026-10-07). A stored variable of a removed type shows an empty select.
const variableValuesTypes = [
  { value: "String", label: "String" },
  { value: "Number", label: "Number" },
  { value: "Boolean", label: "Boolean" },
  { value: "Array", label: "Array " },
  { value: "Link", label: "Link" },
  { value: "faq", label: "faq" },
  { value: "service", label: "service" },
  { value: "infotext", label: "infotext" },
  { value: "bottomblockleft", label: "bottomblockleft" },
  { value: "bottomblockright", label: "bottomblockright" },
  { value: "categorymenu", label: "categorymenu" },
  { value: "sitemap", label: "sitemap" },
];
const variableStatus = [
  { value: "required", label: "Required" },
  { value: "optional", label: "Optional" },
];
const emptyVariable = {
  title: "",
  notes: "",
  type: "content",
  status: "required",
  variable_type: "",
  variable_complex_type: "",
  example: "",
  image: "",
};
const emptyFormChecker = {
  title: false,
  notes: false,
  type: false,
  status: false,
  variable_type: false,
  variable_complex_type: false,
  example: false,
  image: false,
};
const customStyles = {
  control: (base) => ({
    ...base,
    height: 50,
    minHeight: 50,
  }),
};
// Complex value types get a default variable_complex_type when picked; plain
// types (String, Number, faq, …) derive "".
const complexTypeByValueType = {
  Array: "product",
  Link: "link",
};
// The radios shown when the value type is "Array". The quirky id/name values are
// preserved verbatim from the pre-merge markup (each radio has its own name; the
// group behaves as one because `checked` is controlled).
const complexTypeRadios = [
  { value: "product", id: "radioProduct", name: "radioProductt", label: "Products", md: "2" },
  { value: "category", id: "radioCategory", name: "radioCategoryt", label: "Categories", md: "2" },
  { value: "subcategory", id: "radiosubcategory", name: "radiosubcategoryt", label: "Sub categories", md: "2" },
  { value: "links", id: "radioLinks", name: "radioLinkst", label: "Links", md: undefined },
  { value: "posts", id: "radioposts", name: "radiopostst", label: "Posts", md: undefined },
  // SCRUM-1633: no Tags radio — nothing renders a tags complex type since the tag
  // pickers were removed (SCRUM-1624).
  { value: "text", id: "radioText", name: "radioText", label: "Text", md: undefined },
  { value: "content", id: "radioContent", name: "radioContent", label: "Content", md: undefined },
];

/**
 * Does this save error say the title is already taken?
 *
 * The server answers a duplicate title with the field-level error envelope —
 * `{ errors: [{ msg, param: "title", value }] }`, `controllers/variable.js` —
 * and `param` is the documented field for putting a message under the offending
 * input (`docs/Backend_API_Error_Contract.md`, rule 1).
 *
 * Until SCRUM-1323 the server forwarded Mongo's raw error instead, and this form
 * keyed on its `keyValue` property. That field stopped crossing the wire when the
 * catch became `sendError(...)`, so the message silently stopped rendering while
 * the write went on being refused (SCRUM-1375).
 *
 * An error that carries `deletedId` is not a duplicate the editor can see: it is
 * the SCRUM-1820 409, where only a soft-deleted variable holds the title, and the
 * form offers Restore / Start fresh for it instead of "This title already exist".
 */
const isDuplicateTitleError = (saveError) =>
  Array.isArray(saveError?.errors) &&
  saveError.errors.some((e) => e?.param === "title" && !e?.deletedId);

/**
 * The SCRUM-1820 409 body's first error when it names a soft-deleted holder of
 * the title, else null.
 */
const deletedTitleHolder = (body) => {
  const first = Array.isArray(body?.errors) ? body.errors[0] : null;
  return first?.deletedId ? first : null;
};

/**
 * The two-button radio-style toggle used for `type` and `status`.
 */
type ToggleOption = { value: string; label: string };
type ToggleButtonGroupProps = {
  name: string;
  options: ToggleOption[];
  value: unknown;
  onSelect: (value: string) => void;
  size?: string;
};

const ToggleButtonGroup = ({ name, options, value, onSelect, size = undefined }: ToggleButtonGroupProps) => (
  <ButtonGroup className="btn-group-toggle" data-toggle="buttons" size={size}>
    {options.map((option) => (
      <Button
        key={option.value}
        className={classnames({ active: value === option.value })}
        color="secondary"
        onClick={() => onSelect(option.value)}
      >
        <input
          autoComplete="off"
          name={name}
          type="radio"
          value={option.value}
        />
        {option.label}
      </Button>
    ))}
  </ButtonGroup>
);

/**
 * Shared create/update form for GlobalTools variables (SCRUM-899).
 *
 * The former AddVariable/UpdateVariable components were ~85% identical; this is
 * the merged form. `mode` selects the per-mode behaviour that used to differ:
 * - "add": starts empty, POSTs via addVariable, watches errors.add, resets the
 *   form in the save success path (SCRUM-1180), briefly flips clearImage
 *   after an image upload.
 * - "update": starts from (and re-syncs with) props.variable, PUTs via
 *   updateVariable, watches errors.update, shows the stored image in the
 *   uploader, and navigates back via props.switchToVariableListHandler() after
 *   a successful save.
 */
type VariableFormProps = {
  mode: "add" | "update";
  variable?: any; // any: no client type for the variable document yet; the form edits its fields by name
  switchToVariableListHandler?: () => void;
};

const VariableForm = (props: VariableFormProps) => {
  const isAdd = props.mode === "add";
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const saveError = useAppSelector((state) =>
    isAdd ? state.variables.errors.add : state.variables.errors.update
  );
  const [variable, setVariable] = useState<any>(isAdd ? emptyVariable : props.variable); // any: same untyped variable document as the prop
  const [showCheckerResult, setShowCheckerResult] = useState(false);
  const [showDuplicationError, setShowDuplicationError] = useState(false);
  const [formChecker, setFormChecker] = useState(emptyFormChecker);
  const [clearImage, setClearImage] = useState(false);
  // SCRUM-1820: the soft-deleted variable a 409 named, plus the payload that got
  // the 409 — Start fresh re-sends exactly that (image already uploaded).
  const [deletedHolder, setDeletedHolder] = useState<{ id: string, msg: string, payload: any } | null>(null); // any: the payload is the untyped variable document re-sent on Start fresh

  const saveVariable = (variableData) =>
    dispatch(isAdd ? addVariable(variableData) : updateVariable(variableData));
  const failureText = isAdd
    ? "Saving the variable failed. Please try again."
    : "Updating the variable failed. Please try again.";
  const notify = (type, v) =>
    NotificationAlertOptions(
      type,
      isAdd
        ? `${v.title} added successfully`
        : `${v.title} has been updated successfully`
    );
  const afterSaveSuccess = () => {
    // Reset here rather than through a state.variables.added watch-effect
    // (SCRUM-1180): that flag latches to true on the first ADD_VARIABLE and is
    // never set back, so the effect's dependency stopped changing and every add
    // after the first left the submitted values in the form. This runs only
    // after an awaited, non-throwing save, so a failed add still keeps them.
    if (isAdd) setVariable(emptyVariable);
    else props.switchToVariableListHandler?.();
  };

  const onRestore = async () => {
    if (!deletedHolder) return;
    try {
      const restored = await dispatch(restoreVariable(deletedHolder.id));
      setDeletedHolder(null);
      setVariable(emptyVariable);
      NotificationAlertOptions(
        "success",
        `${restored?.title ?? "The variable"} restored`
      );
    } catch (err) {
      // The offer stays on screen; say why the restore failed.
      NotificationAlertOptions("error", getErrorMessage(asCaught(err)?.response?.data));
    }
  };

  const onStartFresh = async () => {
    if (!deletedHolder) return;
    const { id, payload } = deletedHolder;
    try {
      await dispatch(releaseVariable(id));
    } catch (err) {
      NotificationAlertOptions("error", getErrorMessage(asCaught(err)?.response?.data));
      return;
    }
    setDeletedHolder(null);
    try {
      await saveVariable(payload);
      notify("success", payload);
      afterSaveSuccess();
    } catch (err) {
      const body = asCaught(err)?.response?.data;
      NotificationAlertOptions(
        "error",
        hasDisplayableError(body) ? getErrorMessage(body) : failureText
      );
    }
  };

  const submitFormHandler = async (e) => {
    e.preventDefault();
    setDeletedHolder(null);
    if (!validInputsChecker()) {
      setShowCheckerResult(true);
      return;
    }
    // Upload-then-save is a plain awaited sequence now (SCRUM-911): the shared
    // useImageUpload hook uploads the picture and returns its URL, so the save
    // runs inline instead of through the old state.images.url watch-effect +
    // loading-flag dance. uploadImageAction and the save thunks re-throw on
    // failure (SCRUM-815), so a failed upload/save shows the error toast and
    // never confirms/navigates.
    try {
      // Optional chaining: documents created through the API carry no image key
      // at all (unlike UI-created ones, which store image: ""); a bare .data read
      // crashed the submit for those (SCRUM-899, found by e2e).
      if (variable.image?.data) {
        const path = `/VARIABLES/${variable.image.imgName}`;
        const url = await uploadImage(variable.image.data, path);
        try {
          await saveVariable({ ...variable, image: url });
          notify("success", variable);
          afterSaveSuccess();
        } catch (saveErr) {
          // Start fresh re-sends the uploaded URL, not the image data.
          if (saveErr && typeof saveErr === "object") {
            (saveErr as { payload?: unknown }).payload = {
              ...variable,
              image: url,
            };
          }
          throw saveErr;
        } finally {
          // Preserved from the pre-hook effect: AddVariable flips clearImage
          // after an image submit. This is inert — ImageUploadContainer's
          // prevClearRef effect runs before the clear effect on the same render,
          // so the preview is never actually cleared — pinned by the SCRUM-899
          // harness and deliberately NOT "fixed" here.
          setClearImage(true);
          if (isAdd) {
            setTimeout(() => {
              setClearImage(false);
            }, 200);
          }
        }
      } else {
        await saveVariable(variable);
        notify("success", variable);
        afterSaveSuccess();
      }
    } catch (err) {
      const holder = isAdd ? deletedTitleHolder(asCaught(err)?.response?.data) : null;
      if (holder) {
        setDeletedHolder({
          id: String(holder.deletedId),
          msg: String(holder.msg ?? "A deleted variable holds this title"),
          // Set on the error by the image branch above (Start fresh re-sends it).
          payload: (err as { payload?: unknown }).payload ?? variable,
        });
        return;
      }
      // Read the server's reason through the shared normalizer rather than
      // `data.message` alone: since SCRUM-1323 the body is
      // `{ errors: [{ msg }] }`, which has no `.message`, so the old expression
      // fell straight through to the generic fallback and hid what the server
      // said (SCRUM-1263 is the same lesson on other call sites).
      const body = asCaught(err)?.response?.data;
      NotificationAlertOptions(
        "error",
        hasDisplayableError(body)
          ? getErrorMessage(body)
          : asCaught(err)?.message || failureText
      );
    }
  };
  const validInputsChecker = () => {
    const checker = { ...emptyFormChecker };
    Object.keys(variable).forEach((key) => {
      if (
        variable[key] === "" &&
        key !== "image" &&
        key !== "variable_complex_type"
      ) {
        checker[key] = true;
      }
    });
    setFormChecker(checker);
    return Object.keys(checker).every((k) => !checker[k]);
  };

  const selectTypeHandler = (event) => {
    const value = event !== null ? event.value : "";
    setVariable({
      ...variable,
      variable_type: value,
      variable_complex_type: complexTypeByValueType[value] ?? "",
    });
  };

  // Plain render helper (not a nested component — a nested component type would
  // remount the textarea on every keystroke and drop focus).
  const renderTextAreaGroup = ({ label, name, placeholder, rows, missingMessage }) => (
    <FormGroup className="col-md-6">
      <Row>
        <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="2"
        >
          {label}
        </Label>
        <Col md="10">
          <InputGroup className="mb-2">
            <InputGroupText>
              <i className="ni ni-bullet-list-67"></i>
            </InputGroupText>
            <Input
              placeholder={placeholder}
              name={name}
              rows={rows}
              type="textarea"
              value={variable[name]}
              onChange={(e) =>
                setVariable({
                  ...variable,
                  [e.target.name]: e.target.value,
                })
              }
            />
          </InputGroup>
          {showCheckerResult && formChecker[name] ? (
            <div className="text-danger"> {missingMessage} </div>
          ) : null}
          {showCheckerResult && !formChecker[name] ? (
            <div className="text-success">Looks good!</div>
          ) : null}
        </Col>
      </Row>
    </FormGroup>
  );

  // recieved images from child component ImageUpload and saved to state
  const imageUploadRecievedHandler = (img) => {
    let image = "";
    if (img.secure_url) {
      image = img.secure_url;
    } else {
      image = img;
    }
    setVariable({ ...variable, image: image });
  };

  const hasDuplicateTitleError = isDuplicateTitleError(saveError);
  useEffect(() => {
    if (hasDuplicateTitleError) {
      setShowDuplicationError(true);
    }
    return () => {
      setShowDuplicationError(false);
    };
  }, [hasDuplicateTitleError]);
  useEffect(() => {
    if (!isAdd) {
      setVariable(props.variable);
    }
  }, [props.variable]);
  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      <Form onSubmit={submitFormHandler}>
        <div style={{ paddingBottom: "30px" }}>
          <ImageUploadContainer
            ImageUpload={imageUploadRecievedHandler}
            clearImage={clearImage}
            image={isAdd ? undefined : variable.image && variable.image}
          ></ImageUploadContainer>
        </div>
        <Row>
          <FormGroup className="col-md-6">
            <Row>
              <Col md="2">
                <Label className="form-control-label" htmlFor="type">
                  type
                </Label>
              </Col>
              <Col md="10">
                <ToggleButtonGroup
                  name="type"
                  options={variableTypes}
                  value={variable.type}
                  onSelect={(value) => setVariable({ ...variable, type: value })}
                  size="lg"
                />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup className="col-md-6">
            <Row>
              <Col md="2">
                <Label className="form-control-label" htmlFor="status">
                  Status
                </Label>
              </Col>
              <Col md="10">
                <ToggleButtonGroup
                  name="status"
                  options={variableStatus}
                  value={variable.status}
                  onSelect={(value) =>
                    setVariable({ ...variable, status: value })
                  }
                />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup className="col-md-6">
            <Row>
              <Label
                className="form-control-label"
                htmlFor="example-text-input"
                md="2"
              >
                label
              </Label>
              <Col md="10">
                <InputGroup
                  className={classnames("input-group-merge", {
                    focused: variable["title"],
                  })}
                >
                  <InputGroupText>
                    <i className="ni ni-badge text-primary" />
                  </InputGroupText>
                  <Input
                    placeholder="  Insert variable Name"
                    type="text"
                    value={variable["title"]}
                    onChange={(e) =>
                      setVariable({
                        ...variable,
                        [e.target.name]: e.target.value,
                      })
                    }
                    name="title"
                    required
                  />
                </InputGroup>
                {showCheckerResult && formChecker.title ? (
                  <div className="text-danger">
                    {" "}
                    Please insert variable title{" "}
                  </div>
                ) : null}
                {showDuplicationError ? (
                  <div className="text-danger"> This title already exist </div>
                ) : null}
                {showCheckerResult &&
                !formChecker.title &&
                !showDuplicationError ? (
                  <div className="text-success">Title Looks good!</div>
                ) : null}
              </Col>
            </Row>
          </FormGroup>
          {renderTextAreaGroup({
            label: "Notes",
            name: "notes",
            placeholder: "Insert Notes",
            rows: "3",
            missingMessage: "Please insert variable notes",
          })}
          {/* Character number (optional) */}
          <FormGroup className="col-md-6">
            <Row>
              <Label
                className="form-control-label"
                htmlFor="char_number"
                md="2"
              >
                Character number
              </Label>
              <Col md="10">
                <InputGroup className="mb-2">
                  <InputGroupText>
                    <i className="ni ni-collection" />
                  </InputGroupText>
                  <Input
                    id="char_number"
                    placeholder="Max characters (optional)"
                    name="char_number"
                    type="number"
                    min="0"
                    value={variable["char_number"] ?? ""}
                    onChange={(e) =>
                      setVariable({
                        ...variable,
                        [e.target.name]:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </InputGroup>
              </Col>
            </Row>
          </FormGroup>
          {renderTextAreaGroup({
            label: "Example",
            name: "example",
            placeholder: "Insert Examples",
            rows: "2",
            missingMessage: "Please insert variable example",
          })}
          <FormGroup className="col-md-6">
            <Row>
              <Label
                className="form-control-label"
                htmlFor="example-text-input"
                md="2"
              >
                Select value type
              </Label>
              <Col md="10">
                <Select
                  value={variableValuesTypes.filter(
                    (option) => option.value === variable["variable_type"]
                  )}
                  name="type"
                  options={variableValuesTypes}
                  onChange={selectTypeHandler}
                  styles={customStyles}
                  isClearable={true}
                  isSearchable={true}
                  required
                />
                {showCheckerResult && formChecker.variable_type ? (
                  <div className="text-danger">
                    {" "}
                    Please insert variable value type{" "}
                  </div>
                ) : null}
                {showCheckerResult && !formChecker.variable_type ? (
                  <div className="text-success">Looks good!</div>
                ) : null}
              </Col>
            </Row>
            {variable.variable_type === "Array" ? (
              <Row>
                <Label
                  className="form-control-label"
                  htmlFor="example-text-input"
                  md="2"
                >
                  complex type
                </Label>

                {complexTypeRadios.map(({ value, id, name, label, md }) => (
                  <Col key={value} md={md}>
                    <div className="custom-control custom-radio mb-3">
                      <input
                        value={value}
                        className="custom-control-input"
                        id={id}
                        name={name}
                        type="radio"
                        checked={variable.variable_complex_type === value}
                        onChange={(e) =>
                          setVariable({
                            ...variable,
                            variable_complex_type: e.currentTarget.value,
                          })
                        }
                      />
                      <label className="custom-control-label" htmlFor={id}>
                        {label}
                      </label>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : null}
          </FormGroup>
        </Row>
        {/*
          SCRUM-1820: a 409 from POST /api/variables/add names the soft-deleted
          variable that still holds this title. Two ways out.
        */}
        {deletedHolder ? (
          <div className="alert alert-warning" role="alert">
            <p className="mb-2">{deletedHolder.msg}.</p>
            <p className="mb-2">
              <Button
                type="button"
                size="sm"
                color="warning"
                className="mr-2"
                onClick={onRestore}
              >
                Restore
              </Button>
              Restore brings the deleted variable back as it was. This new
              variable is not created.
            </p>
            <p className="mb-0">
              <Button
                type="button"
                size="sm"
                outline
                color="warning"
                className="mr-2"
                onClick={onStartFresh}
              >
                Start fresh
              </Button>
              Start fresh creates this new variable. The deleted one stays
              deleted and gives up its title.
            </p>
          </div>
        ) : null}
        <button
          className="btn btn-outline-primary mr-1 float-right"
          type="submit"
        >
          Save variable
        </button>
      </Form>
    </>
  );
};

export default VariableForm;
